import {
  PROCESSING_STATUSES,
  type ListLinksParams,
  type LinkSortField,
  type ProcessingStatus,
  type SortOrder,
} from '@/types/api';

/**
 * Catalogue filter state. This is the single source of truth for the catalogue view and it
 * round-trips through the URL query string, so every view is shareable and survives a refresh.
 *
 * Only `categories` is multi-valued — that mirrors the API, where `category` is the one
 * repeatable parameter.
 */
export interface CatalogueFilters {
  query: string;
  categories: string[];
  domain: string | null;
  sharerId: string | null;
  channelId: string | null;
  /** ISO date (yyyy-MM-dd) or null. */
  from: string | null;
  to: string | null;
  sort: LinkSortField;
  order: SortOrder;
  archived: boolean;
  status: ProcessingStatus | null;
}

export const DEFAULT_FILTERS: CatalogueFilters = {
  query: '',
  categories: [],
  domain: null,
  sharerId: null,
  channelId: null,
  from: null,
  to: null,
  sort: 'createdAt',
  order: 'desc',
  archived: false,
  status: null,
};

/** The API rejects a free-text search shorter than this, so we simply do not send it. */
export const MIN_QUERY_LENGTH = 2;
export const PAGE_SIZE = 24;

const SORT_FIELDS: readonly LinkSortField[] = ['createdAt', 'firstSharedAt', 'title'];
const SORT_ORDERS: readonly SortOrder[] = ['asc', 'desc'];

export const SORT_OPTIONS: ReadonlyArray<{ value: LinkSortField; label: string }> = [
  { value: 'createdAt', label: 'Recently added' },
  { value: 'firstSharedAt', label: 'First shared' },
  { value: 'title', label: 'Alphabetical' },
];

function isSortField(value: string): value is LinkSortField {
  return (SORT_FIELDS as readonly string[]).includes(value);
}

function isSortOrder(value: string): value is SortOrder {
  return (SORT_ORDERS as readonly string[]).includes(value);
}

function isProcessingStatus(value: string): value is ProcessingStatus {
  return (PROCESSING_STATUSES as readonly string[]).includes(value);
}

/** Accepts yyyy-MM-dd only; anything else is dropped rather than sent to the API. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function readDate(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key)?.trim() ?? '';
  return ISO_DATE.test(raw) ? raw : null;
}

function readOptional(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key)?.trim() ?? '';
  return raw.length > 0 ? raw : null;
}

/** URL -> filter state. Unknown or malformed values fall back to the default. */
export function parseFilters(params: URLSearchParams): CatalogueFilters {
  const sortRaw = params.get('sort')?.trim() ?? '';
  const orderRaw = params.get('order')?.trim() ?? '';
  const statusRaw = params.get('status')?.trim() ?? '';

  const categories = Array.from(
    new Set(
      params
        .getAll('category')
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter((value) => value.length > 0),
    ),
  ).sort();

  return {
    query: params.get('q')?.trim() ?? '',
    categories,
    domain: readOptional(params, 'domain'),
    sharerId: readOptional(params, 'sharer'),
    channelId: readOptional(params, 'channel'),
    from: readDate(params, 'from'),
    to: readDate(params, 'to'),
    sort: isSortField(sortRaw) ? sortRaw : DEFAULT_FILTERS.sort,
    order: isSortOrder(orderRaw) ? orderRaw : DEFAULT_FILTERS.order,
    archived: params.get('archived') === 'true',
    status: isProcessingStatus(statusRaw) ? statusRaw : null,
  };
}

/**
 * Filter state -> URL. Defaults are omitted so a pristine catalogue has a clean `/` URL and
 * two equivalent states always serialize identically (which keeps query keys stable).
 */
export function filtersToSearchParams(filters: CatalogueFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.query.trim()) params.set('q', filters.query.trim());
  for (const category of [...filters.categories].sort()) params.append('category', category);
  if (filters.domain) params.set('domain', filters.domain);
  if (filters.sharerId) params.set('sharer', filters.sharerId);
  if (filters.channelId) params.set('channel', filters.channelId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.sort !== DEFAULT_FILTERS.sort) params.set('sort', filters.sort);
  if (filters.order !== DEFAULT_FILTERS.order) params.set('order', filters.order);
  if (filters.archived) params.set('archived', 'true');
  if (filters.status) params.set('status', filters.status);
  return params;
}

/** Filter state -> API query parameters. */
export function filtersToListParams(filters: CatalogueFilters): ListLinksParams {
  const trimmedQuery = filters.query.trim();
  return {
    query: trimmedQuery.length >= MIN_QUERY_LENGTH ? trimmedQuery : undefined,
    category: filters.categories.length > 0 ? [...filters.categories].sort() : undefined,
    domain: filters.domain ?? undefined,
    sharerId: filters.sharerId ?? undefined,
    channelId: filters.channelId ?? undefined,
    from: filters.from ?? undefined,
    to: filters.to ?? undefined,
    sort: filters.sort,
    order: filters.order,
    archived: filters.archived,
    status: filters.status ?? undefined,
    limit: PAGE_SIZE,
  };
}

/** True when nothing narrows the result set — used to tell "empty catalogue" from "no matches". */
export function hasActiveFilters(filters: CatalogueFilters): boolean {
  return (
    filters.query.trim().length > 0 ||
    filters.categories.length > 0 ||
    filters.domain !== null ||
    filters.sharerId !== null ||
    filters.channelId !== null ||
    filters.from !== null ||
    filters.to !== null ||
    filters.archived ||
    filters.status !== null
  );
}

export function activeFilterCount(filters: CatalogueFilters): number {
  let count = 0;
  if (filters.query.trim()) count += 1;
  count += filters.categories.length;
  if (filters.domain) count += 1;
  if (filters.sharerId) count += 1;
  if (filters.channelId) count += 1;
  if (filters.from) count += 1;
  if (filters.to) count += 1;
  if (filters.archived) count += 1;
  if (filters.status) count += 1;
  return count;
}

/** Everything a filter chip needs: a label and how to remove just that one filter. */
export type FilterChipKind =
  'query' | 'category' | 'domain' | 'sharer' | 'channel' | 'from' | 'to' | 'archived' | 'status';

export interface FilterChip {
  id: string;
  kind: FilterChipKind;
  label: string;
  value: string;
}

export function clearFilter(
  filters: CatalogueFilters,
  kind: FilterChipKind,
  value?: string,
): CatalogueFilters {
  switch (kind) {
    case 'query':
      return { ...filters, query: '' };
    case 'category':
      return { ...filters, categories: filters.categories.filter((slug) => slug !== value) };
    case 'domain':
      return { ...filters, domain: null };
    case 'sharer':
      return { ...filters, sharerId: null };
    case 'channel':
      return { ...filters, channelId: null };
    case 'from':
      return { ...filters, from: null };
    case 'to':
      return { ...filters, to: null };
    case 'archived':
      return { ...filters, archived: false };
    case 'status':
      return { ...filters, status: null };
    default:
      return filters;
  }
}

/** Clearing everything keeps sort/order — those are a view preference, not a filter. */
export function clearAllFilters(filters: CatalogueFilters): CatalogueFilters {
  return { ...DEFAULT_FILTERS, sort: filters.sort, order: filters.order };
}
